<template>
    <section :class="[namespace(), skinStyle]">
        <markets-ui-menu-button
            v-if="isCollapsed"
            :label="menuLabel"
            :skin="skin"
            :size="size"
            @click="dropdownButtonClicked"
        ></markets-ui-menu-button>
        <div :class="mdsNS('button-group')" v-else>
            <button
                v-for="item in menusData"
                type="button"
                :class="btnClasses(item)"
                :value="item.id"
                :key="item.id"
                @click="click(item)"
            >
                {{ item.name }}
            </button>
        </div>
        <markets-ui-popover
            :target="popoverTarget"
            :visible="popoverVisible"
            :skin="skin"
            @hide="hidePopover"
        >
            <markets-ui-list
                :dataModel="menusData"
                :size="size"
                :skin="skin"
                @change="change"
            ></markets-ui-list>
        </markets-ui-popover>
    </section>
</template>
<script>
import utils from '../../utils';
import mixins from '../mixins';
const MODE = {
    AUTO: 'auto',
    COLLAPSE: 'collapse',
    EXTEND: 'extend'
};
export default {
    name: 'markets-ui-menus',
    mixins: [mixins],
    props: {
        dataModel: {
            type: Array,
            default() {
                return [];
            }
        },
        // controle if stay collapsed regardless the container's width
        collapseMode: {
            type: String,
            default: MODE.AUTO
        },
        size: {
            type: String,
            default: 'small'
        },
        label: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isCollapsed: false,
            dropdownButtonName: '',
            popoverTarget: null,
            popoverVisible: false,
            menusData: {}
        };
    },
    created() {
        this.namespace = utils.namespace('ui-menus', false);
    },
    mounted() {
        if (this.collapseMode === MODE.AUTO) {
            this.$nextTick(() => {
                this.listenResize();
            });
        }
    },
    computed: {
        menuLabel() {
            if (this.label) {
                return this.label;
            } else {
                return (
                    (utils.find(this.menusData, item => item.selected) || {})
                        .name || ''
                );
            }
        }
    },
    watch: {
        collapseMode: {
            handler(value) {
                if (value === MODE.AUTO) {
                    this.$nextTick(() => {
                        this.listenResize();
                    });
                } else {
                    utils.resizeObserver.unobserve(
                        this.resizeElment,
                        this._resizeObserverCallback
                    );
                    if (value === MODE.COLLAPSE) {
                        this.isCollapsed = true;
                    } else {
                        this.isCollapsed = false;
                    }
                }
            },
            immediate: true
        },
        dataModel: {
            handler(value) {
                this.menusData = utils.extend(true, [], value);
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        click(item) {
            if (!item.selected) {
                this.$emit('change', item);
            }
        },
        listenResize() {
            this.resizeElment = this.findBindingResizeElement();
            if (this.resizeElment) {
                utils.resizeObserver.observe(
                    this.resizeElment,
                    this._resizeObserverCallback
                );
            }
            this.throttleResize = utils.throttle(() => {
                this.changeLayout();
            }, 200);
            this.$nextTick(() => {
                this.changeLayout();
            });
        },
        change(item) {
            const changeItem = utils.find(this.dataModel, dm => {
                return item.id === dm.id;
            });
            for (const item of this.menusData) {
                if (item.id === changeItem.id) {
                    item.selected = true;
                } else {
                    item.selected = false;
                }
            }
            this.hidePopover();
            this.$emit('change', changeItem);
        },
        btnClasses(item) {
            const cls = ['button', 'button--secondary'];
            if (this.size === 'small') {
                cls.push('button--small');
            }
            if (item.selected) {
                cls.push('button--active');
            }
            return this.mdsNS(cls);
        },
        /**
         * re-calculate the width to see if need collapse
         */
        changeLayout() {
            this.hidePopover();
            this.$nextTick(() => {
                if (this.collapseMode === MODE.AUTO && this.dataModel.length) {
                    //Append a hidden elment with button group to the root element
                    const hiddenGroup = document.createElement('div');
                    [
                        this.mdsNS('button-group'),
                        this.namespace('hidden')
                    ].forEach(item => {
                        hiddenGroup.classList.add(item);
                    });
                    this.$el.appendChild(hiddenGroup);
                    this.menusData.forEach(item => {
                        const btn = document.createElement('button');
                        btn.classList.add(...this.btnClasses(item).split(' '));
                        btn.innerText = item.name;
                        hiddenGroup.appendChild(btn);
                    });
                    //If the sum of all buttons'length > the root element's width, then need collapse
                    let btnWidth = 0;
                    const fakeButtons = hiddenGroup.querySelectorAll('button');
                    const computedStyle = getComputedStyle(fakeButtons[0]);
                    const buttonBorderWidth =
                        +computedStyle.borderLeftWidth.replace('px', '') +
                        Number(computedStyle.borderImageWidth);
                    fakeButtons.forEach(btn => {
                        btnWidth += btn.getBoundingClientRect().width;
                    });
                    if (
                        btnWidth - buttonBorderWidth * fakeButtons.length >=
                        utils.innerSizes(hiddenGroup).width
                    ) {
                        this.isCollapsed = true;
                    } else {
                        this.isCollapsed = false;
                    }
                    this.$el.removeChild(hiddenGroup);
                }
            });
        },
        _resizeObserverCallback({ entry }) {
            this.throttleResize(entry);
        },
        dropdownButtonClicked(target) {
            this.popoverTarget = target;
            this.popoverVisible = !this.popoverVisible;
        },
        hidePopover() {
            this.popoverTarget = null;
            this.popoverVisible = false;
        },
        beforeDestroy() {
            if (this.collapseMode === MODE.AUTO) {
                utils.resizeObserver.unobserve(
                    this.resizeElment,
                    this._resizeObserverCallback
                );
            }
        },
        findBindingResizeElement() {
            if (this.$parent.$el && this.$parent.$el.isSameNode(this.$el)) {
                return this.$el.parentElement;
            } else {
                return this.$parent.$el;
            }
        }
    }
};
</script>
<style lang="scss">
$namespace: 'markets-ui-menus';
.#{$namespace} {
    button {
        white-space: nowrap;
    }
    &-hidden {
        visibility: hidden;
        padding-left: 5px;
    }
}
</style>
